import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ArrowLeft, Unlock, Pencil } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useFetch } from "@/hooks/useFetch";
import { fmtDate } from "@/lib/format";
import { Loading, PageHeader, StatusBadge } from "@/components/common/kit";
import DayView from "@/components/common/DayView";
import CloseDayFlow from "@/components/common/CloseDayFlow";

export default function DayDetail() {
  const { sid, date } = useParams();
  const { data: day, reload } = useFetch(`/day/${sid}/${date}`);
  const [reopen, setReopen] = useState(false);
  const [edit, setEdit] = useState(false);
  const [reason, setReason] = useState("");
  if (!day) return <Loading />;

  const doReopen = async () => {
    try {
      await api.post(`/day/${sid}/${date}/reopen`, { reason });
      toast.success("Day reopened for correction");
      setReopen(false);
      setReason("");
      reload();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <div className="space-y-6">
      <Link to="/admin/closures" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-900"><ArrowLeft className="h-4 w-4" /> Daily closures</Link>
      <PageHeader title={`${day.salesman.full_name} · ${fmtDate(date)}`} subtitle={`${day.salesman.code} · Day breakdown`}
        actions={<>
          <StatusBadge status={day.status} testid="day-status-badge" />
          {day.status === "CLOSED" && <Button variant="outline" className="border-orange-300 text-orange-700" onClick={() => setReopen(true)} data-testid="reopen-day-button"><Unlock className="mr-1.5 h-4 w-4" /> Reopen Day</Button>}
          {day.status !== "CLOSED" && <Button variant="outline" onClick={() => setEdit(!edit)} data-testid="admin-edit-day-button"><Pencil className="mr-1.5 h-4 w-4" /> {edit ? "Back to view" : "Enter closing on behalf"}</Button>}
        </>} />
      {edit && day.status !== "CLOSED" ? (
        <div className="max-w-xl"><CloseDayFlow sid={sid} date={date} onDone={() => { setEdit(false); reload(); }} /></div>
      ) : (
        <>
          {day.status !== "CLOSED" && <p className="rounded border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">This day is not submitted yet. Figures are live from the stock ledger and current draft entries.</p>}
          <DayView day={day} />
        </>
      )}
      <Dialog open={reopen} onOpenChange={setReopen}>
        <DialogContent className="max-w-md" data-testid="reopen-dialog">
          <DialogHeader><DialogTitle>Reopen {fmtDate(date)}</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-600">Sales entries are reversed, receivable is voided and the salesman can correct and resubmit. Previous values are preserved in the audit log.</p>
          <Textarea placeholder="Reason (required) e.g. Incorrect closing stock entered." value={reason} onChange={(e) => setReason(e.target.value)} data-testid="reopen-reason-input" />
          <DialogFooter><Button onClick={doReopen} disabled={reason.trim().length < 3} data-testid="reopen-closure-modal-submit">Reopen Day</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
